import React from 'react';
import { Button, DatePicker, Tag, Typography } from 'antd';
import moment from 'moment';
import { PageHeader } from '../PageHeader';

const { Title, Text } = Typography;

export interface StatusChip {
    label: string;
    value: string | undefined;
}

interface ListPageHeaderProps {
    title: string;
    subtitle: string;
    action?: React.ReactNode;

    /** Fully custom search node (Input, Select, etc.) */
    searchNode: React.ReactNode;

    chips: StatusChip[];
    activeChip: string | undefined;
    onChipChange: (value: string | undefined) => void;

    dates: [string, string] | undefined;
    onDatesChange: (dates: [string, string] | undefined) => void;
    showDateFilter?: boolean;

    totalCount: number;
    entityName: string;
    hasActiveFilters: boolean;
    onClearFilters: () => void;

    children?: React.ReactNode;
}

export const ListPageHeader: React.FC<ListPageHeaderProps> = ({
    title,
    subtitle,
    action,
    searchNode,
    chips,
    activeChip,
    onChipChange,
    dates,
    onDatesChange,
    showDateFilter = true,
    totalCount,
    entityName,
    hasActiveFilters,
    onClearFilters,
    children,
}) => {
    return (
        <PageHeader>
            {/* Row 1: Title + subtitle + optional CTA */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
                <div>
                    <Title level={2} className="mb-0" style={{ fontWeight: 700 }}>
                        {title}
                    </Title>
                    <Text type="secondary">{subtitle}</Text>
                </div>
                {action}
            </div>

            {/* Row 2: Search */}
            <div className="mb-4">{searchNode}</div>

            {/* Row 3: Quick-filter chips + Date picker */}
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-4">
                <div
                    className="flex gap-2 overflow-x-auto pb-1"
                    style={{ WebkitOverflowScrolling: 'touch' } as React.CSSProperties}
                >
                    {chips.map(chip => {
                        const isChecked = activeChip === chip.value;
                        return (
                            <Tag.CheckableTag
                                key={chip.label}
                                checked={isChecked}
                                onChange={() => onChipChange(isChecked ? undefined : chip.value)}
                                style={{
                                    whiteSpace: 'nowrap',
                                    ...(isChecked
                                        ? { backgroundColor: '#1890ff', color: '#fff', borderColor: '#1890ff' }
                                        : { backgroundColor: '#fafafa', color: '#595959', borderColor: '#d9d9d9' }),
                                }}
                            >
                                {chip.label}
                            </Tag.CheckableTag>
                        );
                    })}
                </div>
                {showDateFilter && (
                    <DatePicker.RangePicker
                        size="small"
                        value={dates ? [moment(dates[0]), moment(dates[1])] : undefined}
                        onChange={(_momentValues, stringValues) => {
                            if (stringValues && stringValues[0]) {
                                onDatesChange([stringValues[0], stringValues[1]]);
                            } else {
                                onDatesChange(undefined);
                            }
                        }}
                        allowClear
                        className="shrink-0"
                    />
                )}
            </div>

            {/* Row 4: Stats bar */}
            <div className="flex items-center gap-6 text-sm" style={{ color: '#718096' }}>
                <div>
                    <span className="font-semibold" style={{ color: '#2d3748' }}>
                        {totalCount}
                    </span>{' '}
                    {hasActiveFilters ? `matching ${entityName}` : `total ${entityName}`}
                </div>
                {hasActiveFilters && (
                    <Button type="link" size="small" onClick={onClearFilters}>
                        Clear filters
                    </Button>
                )}
            </div>

            {children}
        </PageHeader>
    );
};
